/**
 * Panel Ajustes SoftPhone: No molestar manual + reglas horarias + acceso a Logs.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  Article as LogsIcon,
  DeleteOutline,
  DoNotDisturbOn,
  Edit,
  NotificationsActive,
  Schedule,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  fetchSoftphoneDndSettings,
  putSoftphoneDndSettings,
  type SoftphoneDndSettings,
} from '../../services/softphone/softphoneApi';
import { sipSoftphone } from '../../services/softphone/SipSoftphone';
import {
  pushSupported, getPushToggleOn, peekPushCategoryDesiredSync,
  enablePushCategory, disablePushCategory, ensurePushReady,
  prefetchPushInfrastructure, pushErrorMessage,
} from '../../services/pushNotificationsService';
import {
  clientTimezone,
  type SoftphoneDndRule,
  type SoftphoneDndScope,
} from '../../utils/softphoneDnd';

const ACCENT = '#2391FF';
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** Switch compacto: ranura fina centrada; el círculo sobresale (sin cortar abajo). */
const switchSx = (onColor: string) => ({
  width: 36,
  height: 20,
  padding: 0,
  overflow: 'visible',
  flexShrink: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 0,
    top: 1,
    left: 1,
    transitionDuration: '200ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: onColor,
        opacity: 1,
        borderColor: onColor,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.45,
      },
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: '#bbb',
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.35,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 18,
    height: 18,
    boxShadow: '0 1px 4px rgba(0,0,0,0.65)',
    backgroundColor: '#f0f0f0',
  },
  '& .MuiSwitch-track': {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    height: 10,
    width: '100%',
    margin: 0,
    transform: 'translateY(-50%)',
    borderRadius: 5,
    backgroundColor: '#4a4a4a',
    opacity: 1,
    border: '1px solid #7a7a7a',
    boxSizing: 'border-box',
    transition: 'background-color 200ms, border-color 200ms',
  },
});

interface SoftphoneSettingsPanelProps {
  onOpenLogs: () => void;
  canAnswerDoorbell: boolean;
}

const SoftphoneSettingsPanel: React.FC<SoftphoneSettingsPanelProps> = ({
  onOpenLogs,
  canAnswerDoorbell,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverTz, setServerTz] = useState('America/Argentina/Buenos_Aires');
  const [manualEnabled, setManualEnabled] = useState(false);
  const [manualScope, setManualScope] = useState<SoftphoneDndScope>('both');
  const [rules, setRules] = useState<SoftphoneDndRule[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<SoftphoneDndRule | null>(null);
  const [pushOn, setPushOn] = useState(() => (pushSupported() ? peekPushCategoryDesiredSync('phone') : false));
  const [pushHydrated, setPushHydrated] = useState(() => !pushSupported() || peekPushCategoryDesiredSync('phone'));
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState<string | null>(null);
  const [pushMsgError, setPushMsgError] = useState(false);

  const scopes = useMemo((): SoftphoneDndScope[] => {
    if (canAnswerDoorbell) return ['phone', 'doorbell', 'both'];
    return ['phone'];
  }, [canAnswerDoorbell]);

  const applyFromApi = useCallback((data: SoftphoneDndSettings) => {
    setServerTz(data.server_timezone);
    setManualEnabled(Boolean(data.manual?.enabled));
    setManualScope((data.manual?.scope as SoftphoneDndScope) || 'phone');
    setRules(Array.isArray(data.rules) ? data.rules : []);
    sipSoftphone.applyDndSettings({
      manualEnabled: Boolean(data.manual?.enabled),
      manualScope: (data.manual?.scope as SoftphoneDndScope) || 'phone',
      rules: data.rules || [],
      serverTimezone: data.server_timezone,
      effective: data.effective,
    });
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSoftphoneDndSettings();
      applyFromApi(data);
    } catch (e: any) {
      setError(e?.message || t('softphone.settings.loadError'));
    } finally {
      setLoading(false);
    }
  }, [applyFromApi, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!pushSupported()) return;
    let cancelled = false;
    void prefetchPushInfrastructure();
    void (async () => {
      const on = await getPushToggleOn('phone');
      if (!cancelled) {
        setPushOn(on);
        setPushHydrated(true);
      }
      try { await ensurePushReady(); } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handlePushToggle = async (checked: boolean) => {
    if (pushBusy || !pushHydrated) return;
    setPushBusy(true);
    const prev = pushOn;
    setPushOn(checked);
    setPushMsgError(false);
    setPushMsg(checked ? t('softphone.settings.pushEnabling') : t('softphone.settings.pushDisabling'));
    try {
      if (checked) {
        await enablePushCategory('phone');
        setPushMsg(t('softphone.settings.pushEnabledOk') as string);
      } else {
        await disablePushCategory('phone');
        setPushMsg(t('softphone.settings.pushDisabledOk') as string);
      }
    } catch (err) {
      setPushOn(prev);
      setPushMsgError(true);
      setPushMsg(pushErrorMessage(err, t));
    } finally {
      setPushBusy(false);
    }
  };

  const persist = async (next: {
    manualEnabled: boolean;
    manualScope: SoftphoneDndScope;
    rules: SoftphoneDndRule[];
  }) => {
    setSaving(true);
    setError(null);
    try {
      const data = await putSoftphoneDndSettings({
        manual_enabled: next.manualEnabled,
        manual_scope: canAnswerDoorbell ? next.manualScope : 'phone',
        rules: next.rules.map((r) => ({
          id: r.id,
          enabled: r.enabled,
          days: r.days,
          start: r.start,
          end: r.end,
          scope: canAnswerDoorbell ? r.scope : 'phone',
          client_timezone: r.client_timezone || clientTimezone(),
        })),
        client_timezone: clientTimezone(),
      });
      applyFromApi(data);
    } catch (e: any) {
      setError(e?.message || t('softphone.settings.saveError'));
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const scopeLabel = (s: SoftphoneDndScope) => {
    if (s === 'doorbell') return t('softphone.settings.scopeDoorbell');
    if (s === 'phone') return t('softphone.settings.scopePhone');
    return t('softphone.settings.scopeBoth');
  };

  const daysLabel = (days: number[]) => {
    if (!days?.length) return '—';
    return days
      .slice()
      .sort((a, b) => a - b)
      .map((d) => t(`softphone.settings.daysShort.${DAY_KEYS[d]}`))
      .join(' · ');
  };

  const openNewRule = () => {
    setEditing({
      id: `new-${Date.now()}`,
      enabled: true,
      days: [0, 1, 2, 3, 4],
      start: '22:00',
      end: '07:00',
      scope: canAnswerDoorbell ? 'both' : 'phone',
      client_timezone: clientTimezone(),
    });
    setEditorOpen(true);
  };

  const saveEditor = async () => {
    if (!editing) return;
    if (!editing.days.length) {
      setError(t('softphone.settings.needDays') as string);
      return;
    }
    if (editing.start === editing.end) {
      setError(t('softphone.settings.badWindow') as string);
      return;
    }
    const exists = rules.some((r) => r.id === editing.id);
    const nextRules = exists
      ? rules.map((r) => (r.id === editing.id ? editing : r))
      : [...rules, editing];
    setEditorOpen(false);
    setEditing(null);
    await persist({ manualEnabled, manualScope, rules: nextRules });
  };

  const tzHints = (
    <>
      <Typography sx={{ color: '#9ec9ff', fontSize: '0.68rem', mt: 1, fontFamily: 'monospace', display: 'block' }}>
        {t('softphone.settings.tzHintPhone', { phone: clientTimezone() })}
      </Typography>
      <Typography sx={{ color: '#9ec9ff', fontSize: '0.68rem', fontFamily: 'monospace', display: 'block' }}>
        {t('softphone.settings.tzHintServer', { server: serverTz })}
      </Typography>
    </>
  );

  const pushCard = (
    <Box
      sx={{
        borderRadius: 2,
        p: 1.75,
        background: 'linear-gradient(180deg, #121820 0%, #0e0e0e 100%)',
        border: pushSupported() && pushOn ? '1px solid rgba(35,145,255,0.45)' : '1px solid #333',
      }}
    >
      {pushSupported() && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <NotificationsActive sx={{ color: pushOn ? ACCENT : '#888', fontSize: 22 }} />
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                {t('softphone.settings.pushTitle')}
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.72rem' }}>
                {t('softphone.settings.pushHint')}
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={pushOn}
            disabled={!pushHydrated || pushBusy}
            onChange={(_, checked) => { void handlePushToggle(checked); }}
            sx={switchSx(ACCENT)}
          />
        </Box>
      )}
      {pushSupported() && pushMsg && (
        <Typography sx={{ color: pushMsgError ? '#ef5350' : '#9ec9ff', fontSize: '0.72rem', mt: 1 }}>
          {pushMsg}
        </Typography>
      )}
      {tzHints}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 2 }}>
        {pushCard}
        <Typography sx={{ color: '#888', p: 2, fontSize: '0.85rem' }}>
          {t('softphone.settings.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 2 }}>
      {error && (
        <Typography sx={{ color: '#ef5350', fontSize: '0.8rem' }}>{error}</Typography>
      )}

      {pushCard}

      {/* Manual DND */}
      <Box
        sx={{
          borderRadius: 2,
          p: 1.75,
          background: 'linear-gradient(180deg, #1e1512 0%, #141414 100%)',
          border: manualEnabled ? '1px solid #ef5350aa' : '1px solid #333',
          boxShadow: manualEnabled ? '0 0 20px rgba(239,83,80,0.2)' : 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <DoNotDisturbOn sx={{ color: manualEnabled ? '#ef5350' : '#888', fontSize: 22 }} />
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                {t('softphone.settings.manualTitle')}
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.72rem' }}>
                {t('softphone.settings.manualHint')}
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={manualEnabled}
            disabled={saving}
            onChange={(_, checked) => {
              setManualEnabled(checked);
              void persist({ manualEnabled: checked, manualScope, rules });
            }}
            sx={switchSx('#ef5350')}
          />
        </Box>        {canAnswerDoorbell && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {scopes.map((s) => (
              <Chip
                key={s}
                size="small"
                label={scopeLabel(s)}
                onClick={() => {
                  if (saving) return;
                  setManualScope(s);
                  void persist({ manualEnabled, manualScope: s, rules });
                }}
                sx={{
                  height: 26,
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  color: manualScope === s ? '#fff' : '#bbb',
                  backgroundColor: manualScope === s ? 'rgba(239,83,80,0.35)' : 'rgba(255,255,255,0.06)',
                  border: manualScope === s ? '1px solid #ef535088' : '1px solid #333',
                }}
              />
            ))}
          </Box>
        )}
        {!canAnswerDoorbell && (
          <Typography sx={{ color: '#888', fontSize: '0.72rem', mt: 1.25 }}>
            {t('softphone.settings.phoneOnlyHint')}
          </Typography>
        )}
      </Box>

      {/* Rules */}
      <Box
        sx={{
          borderRadius: 2,
          p: 1.75,
          background: 'linear-gradient(180deg, #121820 0%, #0e0e0e 100%)',
          border: '1px solid rgba(255,167,38,0.35)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule sx={{ color: '#ffb74d', fontSize: 22 }} />
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                {t('softphone.settings.rulesTitle')}
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.72rem' }}>
                {t('softphone.settings.rulesHint')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={openNewRule}
            disabled={saving}
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(255,167,38,0.25)',
              border: '1px solid #ffb74d66',
              '&:hover': { backgroundColor: 'rgba(255,167,38,0.4)' },
            }}
          >
            <Add sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {rules.length === 0 ? (
          <Typography sx={{ color: '#777', fontSize: '0.8rem', py: 1 }}>
            {t('softphone.settings.rulesEmpty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rules.map((rule) => (
              <Box
                key={rule.id}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: rule.enabled ? '1px solid rgba(255,167,38,0.4)' : '1px solid #2a2a2a',
                  opacity: rule.enabled ? 1 : 0.55,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: '#ffcc80', fontWeight: 600, fontSize: '0.82rem' }}>
                      {rule.start} → {rule.end}
                      {rule.end <= rule.start ? ` · ${t('softphone.settings.overnight')}` : ''}
                    </Typography>
                    <Typography sx={{ color: '#aaa', fontSize: '0.72rem', mt: 0.25 }}>
                      {daysLabel(rule.days)}
                    </Typography>
                    <Chip
                      size="small"
                      label={scopeLabel(rule.scope)}
                      sx={{
                        mt: 0.75,
                        height: 22,
                        fontSize: '0.68rem',
                        backgroundColor: 'rgba(35,145,255,0.2)',
                        color: '#9ec9ff',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Switch
                      checked={rule.enabled}
                      disabled={saving}
                      onChange={(_, checked) => {
                        const next = rules.map((r) => (r.id === rule.id ? { ...r, enabled: checked } : r));
                        setRules(next);
                        void persist({ manualEnabled, manualScope, rules: next });
                      }}
                      sx={switchSx('#ffb74d')}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditing({ ...rule });
                        setEditorOpen(true);
                      }}
                      sx={{ color: '#9ec9ff' }}
                    >
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const next = rules.filter((r) => r.id !== rule.id);
                        setRules(next);
                        void persist({ manualEnabled, manualScope, rules: next });
                      }}
                      sx={{ color: '#ef9a9a' }}
                    >
                      <DeleteOutline sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Logs entry */}
      <Button
        fullWidth
        startIcon={<LogsIcon />}
        onClick={onOpenLogs}
        sx={{
          textTransform: 'none',
          color: '#fff',
          py: 1.1,
          borderRadius: 2,
          background: `linear-gradient(90deg, ${ACCENT}33, rgba(156,39,176,0.25))`,
          border: `1px solid ${ACCENT}66`,
          fontWeight: 600,
          '&:hover': { background: `linear-gradient(90deg, ${ACCENT}55, rgba(156,39,176,0.4))` },
        }}
      >
        {t('softphone.settings.openLogs')}
      </Button>

      {/* Rule editor */}
      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: '#141414',
            backgroundImage: 'linear-gradient(180deg, #1a2433 0%, #121212 40%)',
            border: `1px solid ${ACCENT}55`,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          {t('softphone.settings.editRule')}
          <IconButton size="small" onClick={() => setEditorOpen(false)} sx={{ color: '#888' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {editing && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {DAY_KEYS.map((key, idx) => {
                  const on = editing.days.includes(idx);
                  return (
                    <Chip
                      key={key}
                      size="small"
                      label={t(`softphone.settings.daysShort.${key}`)}
                      onClick={() => {
                        setEditing({
                          ...editing,
                          days: on
                            ? editing.days.filter((d) => d !== idx)
                            : [...editing.days, idx].sort((a, b) => a - b),
                        });
                      }}
                      sx={{
                        height: 28,
                        fontWeight: 600,
                        color: on ? '#fff' : '#aaa',
                        backgroundColor: on ? ACCENT : 'rgba(255,255,255,0.06)',
                      }}
                    />
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label={t('softphone.settings.start')}
                  type="time"
                  value={editing.start}
                  onChange={(e) => setEditing({ ...editing, start: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiInputLabel-root': { color: '#999' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                  }}
                />
                <TextField
                  label={t('softphone.settings.end')}
                  type="time"
                  value={editing.end}
                  onChange={(e) => setEditing({ ...editing, end: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiInputLabel-root': { color: '#999' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                  }}
                />
              </Box>
              {canAnswerDoorbell && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {scopes.map((s) => (
                    <Chip
                      key={s}
                      size="small"
                      label={scopeLabel(s)}
                      onClick={() => setEditing({ ...editing, scope: s })}
                      sx={{
                        height: 26,
                        fontWeight: 600,
                        color: editing.scope === s ? '#fff' : '#bbb',
                        backgroundColor: editing.scope === s ? 'rgba(255,167,38,0.35)' : 'rgba(255,255,255,0.06)',
                        border: editing.scope === s ? '1px solid #ffb74d88' : '1px solid #333',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setEditorOpen(false)} sx={{ color: '#aaa', textTransform: 'none' }}>
            {t('common.cancel', { defaultValue: 'Cancelar' })}
          </Button>
          <Button
            onClick={() => void saveEditor()}
            variant="contained"
            sx={{ textTransform: 'none', backgroundColor: ACCENT }}
          >
            {t('common.save', { defaultValue: 'Guardar' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoftphoneSettingsPanel;
